/**
 * Modal de criação de landing page.
 *
 * A IA é adiantamento, não porteira: se a geração falhar, a página existe com
 * os blocos padrão e o operador segue dali. Criada, a navegação vai direto ao
 * editor — a página nova sem conteúdo é exatamente a que mais precisa dele.
 */

import type { Bloco } from '@egba-do/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api, type Categoria, type Item, type Lista } from '../../api/cliente'
import { AreaTexto, Aviso, Botao, Campo, Modal, Selecao } from '../../componentes/ui'
import type { LandingPagina } from './tipos'

function paraSlug(t: string): string {
  return t
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
}

export function NovaPagina({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const qc = useQueryClient()
  const navegar = useNavigate()
  const [erro, setErro] = useState<string | null>(null)
  const [form, setForm] = useState({ titulo: '', slug: '', categoriaId: '', descricao: '' })
  const [gerarComIa, setGerarComIa] = useState(true)

  const { data: categorias } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => api.get<Lista<Categoria>>('/api/v1/admin/categorias'),
  })

  const { data: ia } = useQuery({
    queryKey: ['estudio-estado'],
    queryFn: () => api.get<Item<{ disponivel: boolean }>>('/api/v1/estudio/estado'),
  })

  const criar = useMutation({
    mutationFn: async () => {
      const nova = await api.post<Item<LandingPagina>>('/api/v1/landing', {
        titulo: form.titulo,
        slug: form.slug || paraSlug(form.titulo),
        categoriaId: form.categoriaId || undefined,
        classeComunicacao: 'obrigacional',
      })

      if (gerarComIa && ia?.dados.disponivel && form.descricao.trim().length >= 20) {
        try {
          const g = await api.post<Item<{ titulo: string; descricaoSeo: string; blocos: Bloco[] }>>(
            '/api/v1/estudio/landing',
            {
              briefing: form.descricao,
              categoria: categorias?.dados.find((c) => c.id === form.categoriaId)?.nome,
            },
          )
          if (g.dados.blocos.length > 0) {
            await api.put(`/api/v1/landing/${nova.dados.id}`, {
              blocos: g.dados.blocos,
              seo: { titulo: g.dados.titulo, descricao: g.dados.descricaoSeo },
            })
          }
        } catch {
          // Silencioso de propósito: a página foi criada, que é o que importa.
        }
      }

      return nova
    },
    onSuccess: (r) => {
      aoFechar()
      setForm({ titulo: '', slug: '', categoriaId: '', descricao: '' })
      void qc.invalidateQueries({ queryKey: ['landings'] })
      void navegar(`/landing/${r.dados.slug}`)
    },
    onError: (e: Error) => setErro(e.message),
  })

  return (
    <Modal aberto={aberto} aoFechar={aoFechar} titulo="Nova landing page">
      <div className="space-y-4">
        {erro && <Aviso tom="perigo">{erro}</Aviso>}

        <Campo
          rotulo="Título"
          obrigatorio
          value={form.titulo}
          onChange={(e) =>
            setForm({
              ...form,
              titulo: e.target.value,
              slug: form.slug || paraSlug(e.target.value),
            })
          }
        />
        <Campo
          rotulo="Endereço"
          value={form.slug}
          onChange={(e) => setForm({ ...form, slug: paraSlug(e.target.value) })}
          ajuda={`A página ficará em /lp/${form.slug || 'endereco'}`}
        />
        <Selecao
          rotulo="Categoria"
          value={form.categoriaId}
          onChange={(e) => setForm({ ...form, categoriaId: e.target.value })}
          opcoes={[
            { valor: '', texto: 'Sem categoria' },
            ...(categorias?.dados ?? []).map((c) => ({ valor: c.id, texto: c.nome })),
          ]}
        />

        {ia?.dados.disponivel && (
          <>
            <label className="flex items-start gap-2.5 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={gerarComIa}
                onChange={(e) => setGerarComIa(e.target.checked)}
                className="mt-0.5 size-4"
              />
              <span>
                Montar a estrutura com IA
                <span className="block text-xs text-slate-500">
                  A IA propõe blocos e texto — e só pode citar as normas que já estão no sistema.
                  Você edita tudo depois.
                </span>
              </span>
            </label>

            {gerarComIa && (
              <AreaTexto
                rotulo="Objetivo da página"
                rows={4}
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                ajuda="Quem lê, o que precisa entender e o que deve fazer. Mínimo de 20 caracteres."
              />
            )}
          </>
        )}
      </div>

      <div className="mt-5 flex justify-end gap-3">
        <Botao variante="secundario" onClick={aoFechar}>
          Cancelar
        </Botao>
        <Botao
          carregando={criar.isPending}
          disabled={form.titulo.trim().length < 3}
          onClick={() => criar.mutate()}
        >
          {criar.isPending && gerarComIa ? 'Gerando…' : 'Criar e abrir editor'}
        </Botao>
      </div>
    </Modal>
  )
}
